// ============================================================
// Leave Type Modal Component
// ============================================================

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Grid,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { X, CalendarDays } from 'lucide-react';

export const LEAVE_TYPE_ENUMS = [
  {
    label: 'Casual Leave',
    name: 'Casual Leave',
    code: 'CL',
    days_per_year: 12,
    isPaid: true,
    description: 'Granted for casual, personal, or unexpected private affairs.',
  },
  {
    label: 'Sick Leave / Medical Leave',
    name: 'Sick Leave',
    code: 'SL',
    days_per_year: 10,
    isPaid: true,
    description: 'Medical leave for illness, medical checkups, or health recovery.',
  },
  {
    label: 'Earned Leave / Privilege Leave',
    name: 'Earned Leave',
    code: 'EL',
    days_per_year: 15,
    isPaid: true,
    description: 'Earned or privilege leave accrued over working days.',
  },
  {
    label: 'Maternity Leave',
    name: 'Maternity Leave',
    code: 'ML',
    days_per_year: 180,
    isPaid: true,
    description: 'Maternity leave for female employees for childbirth and childcare.',
  },
  {
    label: 'Paternity Leave',
    name: 'Paternity Leave',
    code: 'PL',
    days_per_year: 15,
    isPaid: true,
    description: 'Paternity leave for male employees on the birth of a child.',
  },
  {
    label: 'Duty Leave',
    name: 'Duty Leave',
    code: 'DL',
    days_per_year: 10,
    isPaid: true,
    description: 'Leave granted for official school duties, workshops, or training.',
  },
  {
    label: 'Unpaid Leave / Leave Without Pay (LWP)',
    name: 'Unpaid Leave',
    code: 'LWP',
    days_per_year: 30,
    isPaid: false,
    description: 'Leave without pay for extended absence beyond paid quotas.',
  },
  {
    label: 'Compensatory Off (C-Off)',
    name: 'Compensatory Off',
    code: 'CO',
    days_per_year: 5,
    isPaid: true,
    description: 'Compensatory off earned for working on holidays or weekends.',
  },
  {
    label: 'Special Leave',
    name: 'Special Leave',
    code: 'SPL',
    days_per_year: 7,
    isPaid: true,
    description: 'Special purpose leave subject to management approval.',
  },
  {
    label: 'Other',
    name: 'Other',
    code: 'OTHER',
    days_per_year: 12,
    isPaid: true,
    description: 'Custom leave type.',
  },
];

export function LeaveTypeModal({ open, onClose, leaveType = null, onSubmit, loading = false }) {
  const [formData, setFormData] = useState({
    name: 'Casual Leave',
    code: 'CL',
    description: 'Granted for casual, personal, or unexpected private affairs.',
    days_per_year: 12,
    is_paid: true,
    requires_approval: true,
    allow_half_day: true,
    carry_forward: false,
    max_carry_forward_days: 0,
    status: 'ACTIVE',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (leaveType) {
      setFormData({
        name: leaveType.name || 'Casual Leave',
        code: leaveType.code || 'CL',
        description: leaveType.description || '',
        days_per_year: leaveType.days_per_year ?? 12,
        is_paid: leaveType.is_paid ?? true,
        requires_approval: leaveType.requires_approval ?? true,
        allow_half_day: leaveType.allow_half_day ?? true,
        carry_forward: leaveType.carry_forward ?? false,
        max_carry_forward_days: leaveType.max_carry_forward_days ?? 0,
        status: leaveType.status || 'ACTIVE',
      });
    } else {
      setFormData({
        name: 'Casual Leave',
        code: 'CL',
        description: 'Granted for casual, personal, or unexpected private affairs.',
        days_per_year: 12,
        is_paid: true,
        requires_approval: true,
        allow_half_day: true,
        carry_forward: false,
        max_carry_forward_days: 0,
        status: 'ACTIVE',
      });
    }
    setErrors({});
  }, [leaveType, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Leave type name is required';
    if (!formData.code.trim()) newErrors.code = 'Leave code is required';
    if (formData.days_per_year < 0) newErrors.days_per_year = 'Days cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      ...formData,
      name: formData.name.trim(),
      code: formData.code.trim().toUpperCase(),
      description: formData.description.trim(),
      days_per_year: Number(formData.days_per_year),
      max_carry_forward_days: Number(formData.max_carry_forward_days),
    });
  };

  // Combine default enums with any custom leave type name when editing
  const leaveOptions = [...LEAVE_TYPE_ENUMS];
  if (formData.name && !leaveOptions.some((opt) => opt.name === formData.name)) {
    leaveOptions.unshift({
      label: formData.name,
      name: formData.name,
      code: formData.code,
      days_per_year: formData.days_per_year,
      isPaid: formData.is_paid,
      description: formData.description,
    });
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CalendarDays size={20} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {leaveType ? 'Edit Leave Type' : 'Create New Leave Type'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <X size={20} />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                select
                label="Leave Type Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => {
                  const selectedName = e.target.value;
                  const matched = LEAVE_TYPE_ENUMS.find((item) => item.name === selectedName);
                  setFormData((prev) => ({
                    ...prev,
                    name: selectedName,
                    code: matched ? matched.code : prev.code,
                    description: matched ? matched.description : prev.description,
                    days_per_year: matched ? matched.days_per_year : prev.days_per_year,
                    is_paid: matched ? matched.isPaid : prev.is_paid,
                  }));
                  if (errors.name) setErrors((prev) => ({ ...prev, name: null }));
                }}
                error={!!errors.name}
                helperText={errors.name}
                disabled={loading}
              >
                {leaveOptions.map((opt) => (
                  <MenuItem key={opt.name} value={opt.name}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Leave Code"
                placeholder="e.g. CL"
                fullWidth
                required
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value)}
                error={!!errors.code}
                helperText={errors.code}
                disabled={loading}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Days Per Year"
                type="number"
                fullWidth
                required
                inputProps={{ min: 0 }}
                value={formData.days_per_year}
                onChange={(e) => handleChange('days_per_year', e.target.value)}
                error={!!errors.days_per_year}
                helperText={errors.days_per_year}
                disabled={loading}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Status"
                fullWidth
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                placeholder="Details about this leave type..."
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_paid}
                    onChange={(e) => handleChange('is_paid', e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Paid Leave"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requires_approval}
                    onChange={(e) => handleChange('requires_approval', e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Requires Approval"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allow_half_day}
                    onChange={(e) => handleChange('allow_half_day', e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Allow Half Day"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.carry_forward}
                    onChange={(e) => handleChange('carry_forward', e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Allow Carry Forward"
              />
            </Grid>

            {formData.carry_forward && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Max Carry Forward Days"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0 }}
                  value={formData.max_carry_forward_days}
                  onChange={(e) => handleChange('max_carry_forward_days', e.target.value)}
                  disabled={loading}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onClose} disabled={loading} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {leaveType ? 'Update Leave Type' : 'Create Leave Type'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
